import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  AlertV2 as Alert,
  Button,
  CardContent,
  CardHeader,
  CardV2 as Card,
  EAlertV2Type,
  Spinner,
} from '@feathr/components';
import { useBlackboxVersion } from '@feathr/extender/hooks';
import { useAccount, useUser } from '@feathr/extender/state';

import Page from '../Page';

import * as styles from './VersionPage.css';

function VersionPage(): JSX.Element {
  const user = useUser();
  const account = useAccount();
  const { t } = useTranslation();
  const {
    blackboxVersion,
    blackboxVersionError,
    handleReset,
    handlePinpointPurge,
    handleReseed,
    isOnStaging,
  } = useBlackboxVersion();

  const isEmpowered = isOnStaging && account.isTestAccount && (user.isSuperuser || user.isAdmin);

  return (
    <Page title={t('Version')}>
      {isEmpowered && (
        <Card name={'utils'}>
          <CardHeader title={t('Utilities')} />
          <CardContent
            action={
              <Button onClick={handleReset} type={'primary'}>
                {t('Reset')}
              </Button>
            }
            description={t('Reset this account to the minimal required data.')}
          />
          <CardContent
            action={
              <Button onClick={handleReseed} type={'primary'}>
                {t('Reseed')}
              </Button>
            }
            description={t('Reset and reseed this account with default test data.')}
          />
          <CardContent
            action={
              <Button onClick={handlePinpointPurge} type={'primary'}>
                {t('Purge')}
              </Button>
            }
            description={t(
              'Purge the event-based email campaign data from pinpoint for this account. All published Smart, Auto, and Drip campaigns will be stopped.',
            )}
          />
        </Card>
      )}
      <Card name={'frontend'}>
        <CardHeader title={t('Frontend')} />
        <CardContent
          action={GIT_VERSION || 'dev'}
          contentClassName={styles.value}
          description={t('Git commit hash')}
          title={t('Version')}
        />
        <CardContent action={user.name} title={t('Username')} />
        <CardContent action={user.id} title={t('User ID')} />
        <CardContent action={account.name} title={t('Account name')} />
        <CardContent action={account.id} title={t('Account ID')} />
      </Card>
      <Card name={'backend'}>
        <CardHeader title={t('Backend')} />
        {!blackboxVersion && !blackboxVersionError && (
          <CardContent key={'loader'}>
            <Spinner />
          </CardContent>
        )}
        {blackboxVersionError && (
          <CardContent key={'loader'}>
            <Alert
              description={t(
                'Refresh the page to try again. If the problem persists, please contact support.',
              )}
              title={t('Could not fetch version information from the backend')}
              type={EAlertV2Type.danger}
            />
          </CardContent>
        )}

        {blackboxVersion && (
          <>
            <CardContent
              action={blackboxVersion.GIT_COMMIT.trim() || 'dev'}
              contentClassName={styles.value}
              description={t('Git commit hash')}
              key={'git'}
              title={t('Version')}
            />
            <CardContent action={blackboxVersion.DB_ENV} key={'db'} title={t('Database')} />
            <CardContent action={blackboxVersion.ES_ENV} key={'es'} title={t('Elasticsearch')} />
            <CardContent action={blackboxVersion.FLASK_ENV} key={'flask'} title={t('Flask')} />
          </>
        )}
      </Card>
    </Page>
  );
}

export default VersionPage;
